import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaBoxOpen, FaPills, FaStore } from "react-icons/fa";
import { FiArrowLeft, FiTag, FiDollarSign } from "react-icons/fi";

const ResepDetail = () => {
  const { name, outlet } = useParams();
  const navigate = useNavigate();

  const [resepDetail, setResepDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const formattedNameOutlet = `${decodeURIComponent(name)}-${decodeURIComponent(outlet)}`;

  useEffect(() => {
    const fetchResepDetail = async () => {
      setLoading(true);
      try {
        if (!name || !outlet) {
          throw new Error("Parameter URL tidak valid");
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/v1/drug/pack/${formattedNameOutlet}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 404) {
          setError(true);
          setResepDetail(null);
          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        if (!data.success || !data.data) {
          setError(true);
          return;
        }

        setResepDetail(data.data);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchResepDetail();
  }, [formattedNameOutlet, token, name, outlet]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20 min-h-screen">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !resepDetail) {
    return (
      <div className="max-w-xl mx-auto py-16 text-center space-y-4">
        <div className="text-rose-500 font-extrabold text-lg">
          ❌ Data Paket Resep Tidak Ditemukan
        </div>
        <button
          onClick={() => navigate("/obat-paket/paket")}
          className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-2xl shadow-md transition-all"
        >
          Kembali ke Daftar Paket
        </button>
      </div>
    );
  }

  const items = resepDetail.items || resepDetail.drugs || [];

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12">
      {/* Header Bar */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/obat-paket/paket")}
            className="p-2.5 rounded-2xl bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 transition-colors shadow-sm"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
              <span>{resepDetail.name}</span>
            </h1>
            <p className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5">
              <FaStore className="text-emerald-600" />
              <span>Outlet: <strong className="text-gray-700">{(outlet || "").replace(/_/g, " ")}</strong></span>
            </p>
          </div>
        </div>
      </motion.div>

      {/* Pricing Summary Cards */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-5"
      >
        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">
              Harga Jual Paket
            </p>
            <h2 className="text-2xl font-extrabold text-emerald-700 font-mono">
              Rp {(resepDetail.harga_jual || 0).toLocaleString("id-ID")}
            </h2>
          </div>
          <div className="p-3 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <FiDollarSign className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">
              Biaya Tuslah
            </p>
            <h2 className="text-2xl font-extrabold text-teal-600 font-mono">
              Rp {(resepDetail.tuslah || 0).toLocaleString("id-ID")}
            </h2>
          </div>
          <div className="p-3 rounded-2xl bg-teal-50 text-teal-600 border border-teal-100">
            <FiTag className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">
              Biaya Embalase
            </p>
            <h2 className="text-2xl font-extrabold text-amber-600 font-mono">
              Rp {(resepDetail.embalase || 0).toLocaleString("id-ID")}
            </h2>
          </div>
          <div className="p-3 rounded-2xl bg-amber-50 text-amber-600 border border-amber-100">
            <FaBoxOpen className="w-6 h-6" />
          </div>
        </div>
      </motion.div>

      {/* Items Breakdown Table */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 space-y-4"
      >
        <div className="flex items-center justify-between pb-3 border-b border-gray-100">
          <h2 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
            <FaPills className="text-emerald-600 w-4 h-4" />
            <span>Rincian Komponen Obat Racikan ({items.length} Item)</span>
          </h2>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm text-xs">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                <th className="p-3.5 w-12 text-center">No</th>
                <th className="p-3.5">Nama Obat</th>
                <th className="p-3.5 text-center">Jumlah Qty</th>
                <th className="p-3.5 text-right">Harga Satuan</th>
                <th className="p-3.5 text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-gray-400 font-semibold">
                    Belum ada obat terdaftar dalam paket ini
                  </td>
                </tr>
              ) : (
                items.map((item, idx) => {
                  const itemPrice = item.price || item.harga_jual || 0;
                  const itemQty = item.qty || 1;
                  const subtotal = itemPrice * itemQty;

                  return (
                    <tr key={idx} className="hover:bg-emerald-50/20 transition-colors">
                      <td className="p-3.5 text-center font-bold text-gray-400">{idx + 1}</td>
                      <td className="p-3.5 font-bold text-gray-900">{item.name}</td>
                      <td className="p-3.5 text-center font-mono font-extrabold text-emerald-700">
                        {itemQty} pcs
                      </td>
                      <td className="p-3.5 text-right font-mono font-semibold text-gray-700">
                        Rp {itemPrice.toLocaleString("id-ID")}
                      </td>
                      <td className="p-3.5 text-right font-mono font-extrabold text-emerald-800">
                        Rp {subtotal.toLocaleString("id-ID")}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default ResepDetail;
